<template>
    <div class="mx-auto w-full max-w-[1168px] pb-4 px-8 pt-0">
        <template v-if="state === 'loading'">
            <div class="flex mt-8 text-gray-500">Loading Purchase Order...</div>
        </template>
        <template v-else>
            <div class="space-y-4 sm:flex sm:space-x-6 sm:space-y-0 sticky sticky-top top-0 pt-6 pb-4 bg-white z-30">
                <div class="flex-grow flex flex-col">
                    <div class="flex-1">
                        <div class="-m-2 sm:flex"
                             :class="'sm:flex sm:items-start sm:justify-between sm:flex-wrap'">
                            <div class="m-2 flex-grow">
                                <h1
                                    class="text-2xl font-bold text-gray-900"
                                    :key="purchaseOrder.reference"
                                    v-html="'Purchase Order ' + highlight('' + purchaseOrder.fullReference)">
                                </h1>
                                <p
                                    class="text-sm font-medium text-gray-500 mt-1"
                                    v-text="'ID #' + purchaseOrder.id">
                                </p>
                            </div>
                            <div v-if="activeTab==='overview' && purchaseOrder.revisions.length && readonly"
                                 class="m-1 w-48">
                                <ui-select v-model="currentRevision" :options="getRevisionsAsDropDown()"
                                           @change="viewRevision"></ui-select>
                            </div>
                            <div v-if="activeTab==='overview' && currentRevision>=0 && purchaseOrder.revisions.length"
                                 class="m-2">
                                <ui-button-new icon="fas fa-arrow-left"
                                               @click="currentRevision=-1;viewRevision(-1)">
                                    Back to Current
                                </ui-button-new>
                            </div>
                            <div v-if="activeTab==='overview' && currentRevision == -1"
                                 class="m-2 flex flex-shrink flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3">
                                <ui-button-new icon="fas fa-arrow-left"
                                               @click="toggleEditMode(false)"
                                               v-if="!readonly">
                                    Done
                                </ui-button-new>
                                <ui-dropdown placement="bottom-end"
                                             menu-class="p-3 w-72"
                                             :key="userKey"
                                             v-else-if="!purchaseOrder.voided">
                                    <template #default="{ toggle }">
                                        <ui-button-new @click="toggle" primary>
                                            Actions
                                            <i class="flex-shrink-0 ml-2 fa fa-chevron-down"></i>
                                        </ui-button-new>
                                    </template>
                                    <template #menu="{ close }">
                                        <ui-dropdown-item @click.prevent="toggleEditMode(true);close();"
                                                          icon="fa fa-edit"
                                                          v-if="!purchaseOrder.locked">
                                            Edit
                                        </ui-dropdown-item>
                                        <ui-dropdown-divider v-if="!purchaseOrder.locked"></ui-dropdown-divider>
                                        <ui-dropdown-item @click.prevent="revise"
                                                          icon="fa fa-undo"
                                                          v-if="purchaseOrder.status === 'Sent'">
                                            Edit as a new Revision
                                        </ui-dropdown-item>
                                        <ui-dropdown-divider
                                            v-if="purchaseOrder.status === 'Sent'"></ui-dropdown-divider>
                                        <ui-dropdown-item @click.prevent="duplicate"
                                                          icon="fa fa-undo">
                                            Duplicate as new Purchase Order
                                        </ui-dropdown-item>
                                        <!--
                                        <ui-dropdown-item icon="fas fa-computer-classic"
                                                          v-if="purchaseOrder.status === 'Sent'"
                                                          @click="pushToClassic(); close()">
                                            Push to Classic Purchase Orders
                                        </ui-dropdown-item>
                                        -->
                                        <ui-dropdown-divider></ui-dropdown-divider>
                                        <ui-dropdown-item @click="openSendModal"
                                                          icon="fas fa-route"
                                                          v-if="currentRevision == -1 && (purchaseOrder.status === 'Draft' || purchaseOrder.status === 'Sent') && readonly && !purchaseOrder.voided">
                                            Send
                                        </ui-dropdown-item>
                                        <ui-dropdown-item @click="openMarkAsSentModal"
                                                          icon="fas fa-check"
                                                          v-if="currentRevision == -1 && (purchaseOrder.status === 'Draft') && readonly && !purchaseOrder.voided">
                                            Manually Mark as Sent
                                        </ui-dropdown-item>
                                        <ui-dropdown-divider
                                            v-if="currentRevision == -1 && (purchaseOrder.status === 'Draft' || purchaseOrder.status === 'Sent') && readonly && !purchaseOrder.voided"></ui-dropdown-divider>
                                        <ui-dropdown-item @click.prevent="openDeleteModal(); close();"
                                                          danger
                                                          icon="fa fa-trash"
                                                          v-if="purchaseOrder.status === 'Draft' && (purchaseOrder.revisions.length === 0)">
                                            Delete
                                        </ui-dropdown-item>
                                        <ui-dropdown-item @click.prevent="openDeleteRevisionModal();close();"
                                                          danger
                                                          icon="fas fa-trash"
                                                          v-else-if="purchaseOrder.status === 'Draft'">
                                            Delete this Revision
                                        </ui-dropdown-item>
                                        <ui-dropdown-item @click.prevent="openVoidModal();close();"
                                                          danger
                                                          icon="fas fa-times-circle"
                                                          v-else>
                                            Mark as VOID
                                        </ui-dropdown-item>
                                    </template>
                                </ui-dropdown>
                            </div>
                        </div>
                    </div>
                    <div class="flex-1 pt-4 m-0 p-0">
                        <nav class="sm:hidden mb-0">
                            <ui-select v-model="activeTab" :options="tabs"></ui-select>
                        </nav>
                        <nav class="hidden sm:flex items-center space-x-6 mb-0">
                            <div v-for="tab in tabs" :key="tab.value">
                                <button
                                    class="px-3 py-2 font-medium text-sm rounded-md"
                                    :class="isActive(tab.value) ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'"
                                    v-text="tab.label"
                                    @click="select(tab.value)"
                                ></button>
                            </div>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- PO section header (if applicable). -->
            <div v-if="isActive('overview')" :key="overviewRenderKey">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Supplier</label>
                        <div v-if="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly"
                             class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 bg-gray-50 whitespace-pre"
                             :class="purchaseOrder.supplier ? 'text-gray-500' : 'text-gray-300'"
                             v-html="purchaseOrder.supplier ? highlight(purchaseOrder.supplier) : 'No supplier'"
                        ></div>
                        <ui-dropdown menu-class="p-3 w-96" v-else>
                            <template #default="{ open,close }">
                                <ui-input class="" field="bg-gray-50 pr-10"
                                          placeholder="Supplier name..."
                                          @focus="open"
                                          @input="open(); save()"
                                          v-model="purchaseOrder.supplier">
                                </ui-input>
                                <div
                                    class="absolute right-0 inset-y-0 h-full flex items-center justify-center p-3 text-gray-400">
                                    <i class="fas fa-search"></i>
                                </div>
                            </template>
                            <template #menu="{ close }">
                                <div class="p-1 max-h-64 overflow-y-auto w-full z-30">
                                    <div v-if="supplierSearch.package.length > 0">
                                        <div class="ml-0 text-xs font-medium text-gray-400 mb-1">Existing Package
                                            Suppliers
                                        </div>
                                        <div class="ml-1 text-xs mb-1 mr-3">
                                            <div
                                                v-for="supplier in supplierSearch.package"
                                                class="flex items-center space-x-1 text-left w-full rounded-md px-2 py-1 focus:outline-none cursor-pointer text-gray-600 hover:bg-blue-50"
                                                @click="purchaseOrder.supplier = supplier; save(); close()"
                                                v-text="supplier"
                                            >
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="supplierSearch.others.length > 0">
                                        <div class="ml-0 text-xs font-medium text-gray-400 mb-1">Other Existing
                                            Suppliers
                                        </div>
                                        <div class="ml-1 text-xs mb-1 mr-3">
                                            <div
                                                v-for="supplier in supplierSearch.others"
                                                class="flex items-center space-x-1 text-left w-full rounded-md px-2 py-1 focus:outline-none cursor-pointer text-gray-600 hover:bg-blue-50"
                                                @click="purchaseOrder.supplier = supplier; save(); close()"
                                                v-text="supplier"
                                            >
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="supplierSearch.others.length + supplierSearch.package.length === 0">
                                        <div
                                            class="text-gray-400 text-xs font-italic p-3 justify-center align-middle items-center flex">
                                            No matching suppliers found
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </ui-dropdown>
                    </div>
                    <div :key="datePickerRenderKey">
                        <label class="block text-sm font-medium text-gray-700">Date</label>
                        <div v-if="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly"
                             class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 text-gray-500 bg-gray-50 whitespace-pre"
                             v-html="highlight(moment(purchaseOrder.date).format('MMMM Do, YYYY'))">
                        </div>
                        <ui-date-picker name="dateTime"
                                        v-model="purchaseOrder.date"
                                        class="xl:col-span-6"
                                        placeholder="What is the PO date?"
                                        :error="dateTimeError"
                                        :with-time="false"
                                        v-else
                                        @change="save"
                                        @input="dateTimeError = ''">
                        </ui-date-picker>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Supplier Reference</label>
                        <div v-if="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly"
                             class="m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 bg-gray-50 whitespace-pre"
                             :class=" purchaseOrder.supplierReference ? 'text-gray-500' : 'text-gray-300'"
                             v-html="purchaseOrder.supplierReference ? highlight(purchaseOrder.supplierReference) : 'No supplier reference'">
                        </div>
                        <ui-input
                            :class="purchaseOrder.status !== 'Draft' || purchaseOrder.voided ? 'bg-gray-50' : ''"
                            placeholder="Supplier reference..."
                            @input="save"
                            v-else
                            v-model="purchaseOrder.supplierReference"
                        ></ui-input>
                    </div>
                    <div v-if="!perItemToggle">
                        <label class="block text-sm font-medium text-gray-700">Package</label>
                        <div
                            class="p-1 relative rounded-md block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-200 placeholder-gray-400 mt-1 shadow-sm form-control">
                            <purchase-order-package-search
                                #default="{ close, open, query, setQuery }"
                                ref="packageSearch"
                                @selected:existingPackageItem="setAllPackage"
                                :current="allPackageName"
                                class="text-xs mx-0 mt-0.5 p-0 border-none text-blue-600"
                                menuClass="w-96">
                                <div class="flex truncate my-0">
                                    <div
                                        class="border rounded-md text-xs px-1.5 py-0.5 bg-gray-50 text-blue-600 ml-1 my-1 truncate whitespace-nowrap"
                                        :class="!readonly ? 'hover:underline cursor-pointer' : ''"
                                        @click="openPackageSearch(open)">
                                            <span
                                                class="fas mr-1"
                                                :class="allPackageReference ? 'fa-cube' : 'fa-warehouse'"></span>
                                        <span
                                            v-text="allPackageReference ? allPackageName : 'Not related to a package'"></span>
                                    </div>
                                </div>
                            </purchase-order-package-search>
                        </div>
                    </div>
                    <div v-if="!perItemToggle">
                        <label class="block text-sm font-medium text-gray-700">Delivery / Collection Address</label>
                        <div
                            class="p-1 relative rounded-md block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-200 placeholder-gray-400 mt-1 shadow-sm form-control">
                            <purchase-order-address-select
                                #default="{ close, open, query, setQuery }"
                                ref="addressSearch"
                                @selected:existingAddress="setAllAddresses"
                                @updateAddresses="updateAllAddresses"
                                @toggleCollection="toggleAllCollection"
                                @clearAddress="clearAllAddress"
                                @addAddress="addAllAddress"
                                :package-reference="purchaseOrder.items.length?purchaseOrder.items[0].packageReference:purchaseOrder.defaultPackageReference"
                                :current="allDeliveryOption"
                                :addresses="addresses"
                                class="text-xs mx-0 mt-0.5 p-0 border-none text-blue-600 w-"
                                menuClass="w-[36em]">
                                <div class="flex truncate my-0">
                                    <div
                                        class="border rounded-md text-xs px-1.5 py-0.5 bg-gray-50 text-blue-600 ml-1 my-1 truncate whitespace-nowrap"
                                        :class="!readonly ? 'hover:underline cursor-pointer' : ''"
                                        @click="openAddressSearch(open)">
                                            <span class="fas mr-1"
                                                  :class=" !allDeliveryOption ? 'fa-question text-gray-400' : allDeliveryOption.isDelivery ? 'fa-truck text-blue-500' : 'fa-people-carry text-blue-500'">
                                            </span>
                                        <span class=""
                                              @click="openAddressSearch(open)"
                                              :class="(allDeliveryOption && allDeliveryOption.address ? 'text-blue-500' : 'text-gray-400') + (!readonly ? ' hover:underline cursor-pointer' : '')">
                                            <template v-if="allDeliveryOption && allDeliveryOption.address">
                                                <span class="pr-1"
                                                      v-for="line in addressLines(allDeliveryOption)"
                                                      v-text="line">
                                                </span>
                                            </template>
                                            <div v-else class="inline-block">No address set</div>
                                            <span
                                                class="pr-1"
                                                v-if="allDeliveryOption && allDeliveryOption.contactName">-</span>
                                            <span class="mx-0"
                                                  v-if="allDeliveryOption && allDeliveryOption.contactName"
                                                  v-text="allDeliveryOption.contactName +(allDeliveryOption.contactNumber?' (' +allDeliveryOption.contactNumber +')':'')"
                                            ></span>
                                          </span>
                                    </div>
                                </div>
                            </purchase-order-address-select>
                        </div>
                    </div>
                    <div v-if="!perItemToggle">
                        <label class="block text-sm font-medium text-gray-700">Requested Delivery Date</label>
                        <div
                            class="p-1 relative rounded-md block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-200 placeholder-gray-400 mt-1 shadow-sm form-control">
                            <div class="flex truncate my-0">
                                <div
                                    class="border rounded-md text-xs px-1.5 py-0.5 bg-gray-50 text-blue-600 ml-1 my-1 truncate whitespace-nowrap"
                                    :class="!readonly ? 'hover:underline cursor-pointer' : ''"
                                >
                                    <div class="relative inline" @click="openDatePicker">
                                        <ui-date-picker
                                            v-model="allDate"
                                            ref="datepicker"
                                            :with-time="false"
                                            class="absolute top top-0 left-0 opacity-0 block max-h-4 max-w-0 overflow-hidden"
                                            field="absolute top top-0 left-0 opacity-0 block max-h-4 max-w-0 overflow-hidden"
                                            @input="updateAllDates"
                                        ></ui-date-picker>
                                        <span
                                            :class="(allDeliveryOption && allDeliveryOption.date ? 'text-blue-500' : 'text-blue-500') + (!readonly ? ' hover:underline cursor-pointer' : '') "
                                            v-text="(allDeliveryOption && allDeliveryOption.date) ? moment(allDeliveryOption.date).format('MMMM Do, YYYY') : 'ASAP'">
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!readonly">
                    <div class="flex w-auto mt-2">
                        <ui-toggle
                            class="inline-block w-auto p-0.5 mt-1 rounded-xl"
                            field=" whitespace-nowrap"
                            text="Edit items package and delivery information separately"
                            v-model="perItemToggle"
                            @input="togglePerItem"
                        ></ui-toggle>
                    </div>
                </div>
                <!-- ################################### ADDITIONAL INFO -->
                <div class="mt-6">
                    <div class="col-span-full"
                    <div class="col-span-full"
                         v-if="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly ">
                        <label class="block text-sm font-medium text-gray-700 mb-1"
                               v-text="'Additional Information'">
                        </label>
                        <div
                            class="min-h-[76px] m-0 px-3 py-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 text-gray-500 bg-gray-50 whitespace-pre"
                            v-html="highlight(purchaseOrder.additionalInformation)">
                        </div>
                    </div>
                    <ui-textarea label="Additional Information"
                                 autoresize
                                 v-model="purchaseOrder.additionalInformation"
                                 placeholder="Additional Information for this Purchase Order..."
                                 @input="save"
                                 v-else
                                 class="xl:col-span-12"
                    ></ui-textarea>
                </div>
                <!-- ################################### Inline FILES -->
                <div class="w-full mb-2 flex mt-6">
                    <div class="flex-grow">
                        <ui-input-file-inline label="Purchase Order Files"
                                              name="media[]"
                                              :filesystemId="purchaseOrder.inlineFilesystemId"
                                              :readonly="readonly"
                                              :attachUrl="attachUrl"
                                              :detachUrl="detachUrl"
                                              :downloadUrl="downloadUrl"
                                              :current-files="getPurchaseOrderFiles"></ui-input-file-inline>
                    </div>
                </div>
                <!-- ################################### Group Tasks -->
                <div v-if="!readonly">
                    <div class="flex w-auto mt-2">
                        <ui-toggle
                            class="inline-block w-auto p-0.5 mt-1 rounded-xl"
                            field=" whitespace-nowrap"
                            text="Group items into tasks"
                            v-model="purchaseOrder.useGroups"
                            @input="update()"
                        ></ui-toggle>
                    </div>
                </div>
                <!-- ################################### ITEMS -->
                <div class="mt-6" :key="'item'+itemsKey">
                    <div
                        class="flex border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                        <div class="w-6 flex-shrink-0"></div>
                        <div class="w-8 -ml-px border-r border-gray-200 px-1.5 py-1">
                            #
                        </div>
                        <div class="flex-1 border-r border-gray-200 px-2 py-1 min-w-24">
                            Description
                        </div>
                        <div class="w-20 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Qty
                        </div>
                        <div class="w-16 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Unit
                        </div>
                        <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Rate
                        </div>
                        <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Net
                        </div>
                        <div class="w-20 -ml-px border-r border-gray-200 pl-1.5 pr-0.5 py-1">
                            Discount
                            <div class="inline-block text-gray-400 hover:text-blue-400 text-xs cursor-pointer"
                                 @click="toggleDiscountMode">
                                <span class="fas fa-random"></span>
                            </div>
                        </div>
                        <div class="w-16 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Vat %
                        </div>
                        <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Vat
                        </div>
                        <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">
                            Total
                        </div>
                        <div class="w-6 flex-shrink-0"></div>
                    </div>
                    <template v-if="!purchaseOrder.useGroups">
                        <draggable draggable=".draggable"
                                   :list="[]"
                                   group="item"
                                   ref="dropTarget"
                                   handle=".handle"
                                   @end="drop">
                            <purchase-order-item v-for="(item, index) in purchaseOrder.items"
                                                 :readonly="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly"
                                                 :highlight="highlight"
                                                 :index="index"
                                                 :item="item"
                                                 :first="index === 0"
                                                 :addresses="addresses"
                                                 :purchase-order="purchaseOrder"
                                                 :showDeliveryOptions="perItemToggle"
                                                 :ref="'item-'+item.id"
                                                 :discountAsPercentage="discountAsPercentage"
                                                 @updated="save"
                                                 @addAddress="addAddress"
                                                 @setAddress="setAddress"
                                                 @updateAddresses="updateAddresses"
                                                 @clearAddress="clearAddress"
                                                 @setAllVatTo="setAllVatTo"
                                                 :key="item.id">
                            </purchase-order-item>
                        </draggable>
                        <div class="flex" v-if="purchaseOrder.status === 'Draft' && !readonly">
                            <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                                <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                            </div>
                            <div class="w-8 -ml-px px-1.5 py-1 -my-px border-t"></div>
                            <div class="flex-1 -ml-px">
                                <input placeholder="Add a new item..."
                                       class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                                       type="text"
                                       v-model="newItem"
                                       @input="addItem(-1)"/>
                            </div>
                            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1"></div>
                        </div>
                    </template>
                    <template v-else>
                        <draggable draggable=".draggable"
                                   :list="purchaseOrder.tasks"
                                   group="tasks"
                                   handle=".taskhandle"
                                   @end="dropTask"
                        >
                            <div class="mt-6 draggable" v-for="(task,tIndex) in purchaseOrder.tasks">
                                <div class="flex-1 -mb-px flex border-t bg-blue-50" :key="taskRenderKey">
                                    <div class="w-6 flex-shrink-0 py-1.5 my-px bg-blue-50">
                                        <div class="w-5 h-5 flex items-center">
                                            <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move pl-1 taskhandle"
                                               v-if="!readonly"></i>
                                        </div>
                                    </div>
                                    <div
                                        v-if="readonly"
                                        class="text-sm -ml-px my-px px-2 pt-1.5 pb-2 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold text-gray-900 whitespace-pre-wrap bg-blue-50"
                                        v-text="task"
                                    >
                                    </div>
                                    <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'" v-else>
                <textarea
                    placeholder="Untitled task..."
                    class="text-sm -ml-px mt-px -mb-px px-2 py-1.5 bg-blue-50 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold border-none text-gray-900"
                    type="text"
                    ref="task"
                    :disabled="readonly"
                    v-model="purchaseOrder.tasks[tIndex]"
                    @change="update"
                    rows="1"
                ></textarea>
                                    </autoresizable-textarea>
                                    <div class="w-6 flex-shrink-0 py-1.5 my-px bg-blue-50" v-if="!readonly">
                                        <ui-dropdown menu-class="p-3 w-96" v-if="purchaseOrder.tasks.length > 1">
                                            <template #default="{ toggle }">
                                                <button @click="toggle"
                                                        class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                                    <i class="fa fa-ellipsis-h fa-fw pl-1 pr-1"></i>
                                                </button>
                                            </template>
                                            <template #menu="{ close }">
                                                <template v-if="!purchaseOrder.items.some((i) => i.task === tIndex)">
                                                    <ui-dropdown-item icon="fas fa-trash" v-if="tIndex > 0"
                                                                      @click="deleteTask(tIndex,'clear');close();">
                                                        Remove Task
                                                    </ui-dropdown-item>
                                                </template>
                                                <template v-else>
                                                    <ui-dropdown-item icon="fas fa-trash" danger
                                                                      @click="deleteTask(tIndex,'clear');close();">
                                                        Delete Task and all items
                                                    </ui-dropdown-item>
                                                    <ui-dropdown-divider></ui-dropdown-divider>
                                                    <ui-dropdown-item icon="fas fa-arrow-up" v-if="tIndex > 0"
                                                                      @click="deleteTask(tIndex,'up');close();"> Remove
                                                        Task (Merge items Up)
                                                    </ui-dropdown-item>
                                                    <ui-dropdown-item icon="fas fa-arrow-down"
                                                                      v-if="tIndex < purchaseOrder.tasks.length-1"
                                                                      @click="deleteTask(tIndex,'down');close();">
                                                        Remove Task (Merge items Down)
                                                    </ui-dropdown-item>
                                                </template>
                                            </template>
                                        </ui-dropdown>
                                    </div>
                                </div>
                                <draggable draggable=".draggable"
                                           :list="[]"
                                           ref="dropTarget"
                                           group="item"
                                           handle=".handle"
                                           @end="drop">
                                    <purchase-order-item
                                        v-for="(item, index) in purchaseOrder.items" v-if="item.task === tIndex"
                                        :readonly="purchaseOrder.status !== 'Draft' || purchaseOrder.voided || readonly"
                                        :highlight="highlight"
                                        :index="index"
                                        :item="item"
                                        :first="(index ===0) && (tIndex===0)"
                                        :addresses="addresses"
                                        :purchase-order="purchaseOrder"
                                        :showDeliveryOptions="perItemToggle"
                                        :discountAsPercentage="discountAsPercentage"
                                        :ref="'item-'+item.id"
                                        @updated="save"
                                        @addAddress="addAddress"
                                        @setAddress="setAddress"
                                        @updateAddresses="updateAddresses"
                                        @clearAddress="clearAddress"
                                        :key="item.id">
                                    </purchase-order-item>
                                </draggable>
                                <div class="flex" v-if="purchaseOrder.status === 'Draft' && !readonly">
                                    <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                                        <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                                    </div>
                                    <div class="w-8 -ml-px px-1.5 py-1 -my-px border-t"></div>
                                    <div class="flex-1 -ml-px">
                                        <input placeholder="Add a new item..."
                                               class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                                               type="text"
                                               v-model="newItem"
                                               @input="addItem(tIndex)"/>
                                    </div>
                                    <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1"></div>
                                </div>
                            </div>
                        </draggable>
                    </template>
                    <div class="flex items-center mt-4 mb-1" v-if="!readonly && purchaseOrder.useGroups">
                        <div class="w-6 flex-shrink-0">
                            <i class="fa fa-plus text-sm text-gray-400 pl-1"></i>
                        </div>
                        <div class="flex-1">
                            <input
                                placeholder="Add a new task..."
                                class="text-sm -ml-px -my-px px-2 py-1.5 border-white font-bold text-gray-900 shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                                type="text"
                                v-model="newTask.name"
                                @input="addTask"
                            >
                        </div>
                    </div>
                    <div class="flex">
                        <div class="flex flex-grow py-1.5 -my-px"></div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b">Net</div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b" v-text="purchaseOrder.netTotal"></div>
                        <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1"></div>
                    </div>
                    <div class="flex">
                        <div class="flex flex-grow py-1.5 -my-px"></div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b">VAT</div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b" v-text="purchaseOrder.vatTotal"></div>
                        <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1"></div>
                    </div>
                    <div class="flex">
                        <div class="flex flex-grow py-1.5 -my-px"></div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b">Gross</div>
                        <div class="w-24 -ml-px px-1.5 py-1 border-b" v-text="purchaseOrder.total"></div>
                        <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1"></div>
                    </div>
                </div>
                <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend"
                          :injectedData="revisionData"
                          :key="sentlogRenderKey"
                          v-if="purchaseOrder.sendLog.length"></send-log>
                <ui-data-grid-item class="mt-8"
                                   label="Quick action"
                                   wide
                                   v-if="currentRevision == -1 && (purchaseOrder.status === 'Draft' || purchaseOrder.status === 'Sent') && readonly && !purchaseOrder.voided">
                    <div class="flex flex-col md:flex-row flex-wrap -m-2">
                        <ui-button-new class="m-2"
                                       icon="fas fa-route"
                                       extra
                                       @click="openSendModal">Send
                        </ui-button-new>
                        <ui-button-new class="m-2" v-if="purchaseOrder.status === 'Sent'"
                                       icon="fas fa-undo"
                                       @click="revise">Edit as a new Revision
                        </ui-button-new>
                        <ui-button-new class="m-2"
                                       icon="fas fa-file-pdf"
                                       @click="download">Download as PDF
                        </ui-button-new>
                    </div>
                </ui-data-grid-item>
                <ui-data-grid class="mt-6">
                    <ui-data-grid-item label="Created By">{{ purchaseOrder.createdBy }} on
                        {{ purchaseOrder.createdOn }}
                    </ui-data-grid-item>
                    <ui-data-grid-item label="Last Updated By" v-if="purchaseOrder.lastUpdatedBy">
                        {{ purchaseOrder.lastUpdatedBy }} on {{ purchaseOrder.lastUpdatedOn }}
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>
            <div v-else-if="isActive('notes')" style="height:calc(100vh - 360px);">
                <notes-v2 :get-url="purchaseOrder.getNotesUrl()"
                          :post-url="purchaseOrder.postNotesUrl()"></notes-v2>
            </div>
            <div v-else-if="isActive('files')" style="height:calc(100vh - 460px);">
                <fs filesystem-title="" path="" :filesystem-id="purchaseOrder.filesystemId"></fs>
            </div>
        </template>
    </div>
</template>
<script>
import draggable from "vuedraggable";
import {PurchaseOrder} from "@models";
import StatusPanel from "../../panels/StatusPanel.vue";
import UiInput from "../../ui/UiInput.vue";
import UiHeader from "../../ui/UiHeader.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import PurchaseOrderItem from "./PurchaseOrderItem.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import MetadataPanel from "../../panels/MetadataPanel.vue";
import PurchaseOrderAddressPanel from "./PurchaseOrderAddressPanel.vue";
import {cloneDeep, debounce, isNull} from "lodash";
import {uuid} from "../../../utils";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import moment from "moment";
import PurchaseOrderPackageSearch from "./PurchaseOrderPackageSearch.vue";
import PurchaseOrderAddressSelect from "./PurchaseOrderAddressSelect.vue";
import UiToggle from "../../ui/UiToggle.vue";
import NotesV2 from "../../NotesV2.vue";
import UiSelect from "../../ui/UiSelect.vue";
import SendLog from "../../send-log/SendLog.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDropdown from "../../ui/UiDropdown.vue";
import Fs from "../../filesystem/Fs.vue";
import UiInputFileInline from "../../filesystem/inline/UiInputFileInline.vue";

export default {
    components: {
        UiInputFileInline,
        Fs,
        UiDropdown,
        AutoresizableTextarea,
        SendLog,
        UiSelect,
        NotesV2,
        UiToggle,
        PurchaseOrderAddressSelect,
        PurchaseOrderPackageSearch,
        UiDataGridItem,
        UiDataGrid,
        UiDropdownDivider,
        UiDropdownItem,
        PurchaseOrderAddressPanel,
        MetadataPanel,
        UiButtonNew,
        PurchaseOrderItem,
        UiDatePicker,
        UiHeader,
        draggable,
        UiInput,
        StatusPanel,
    },
    props: {
        item: PurchaseOrder,
        removeItem: Function,
        fetch: Function,
        updateItems: Function,
        search: "",
    },
    data() {
        return {
            state: "loading",
            purchaseOrder: {},
            dateTimeError: "",
            newItem: "",
            debouncedUpdate: debounce(this.update, 300),
            addresses: [],
            addressPanelKey: 0,
            readonly: true,
            datePickerRenderKey: 0,
            metadataRenderKey: 0,
            userKey: 0,
            perItemToggle: false,
            allDeliveryOption: null,
            allPackageReference: null,
            allPackageName: null,
            allDate: null,
            defaultItem: {},
            newTask: {name: ''},
            moment,
            discountAsPercentage: true,
            activeTab: 'overview',
            taskRenderKey: 0,
            currentRevision: -1,
            sentlogRenderKey: 0,
            overviewRenderKey: 0,
            suppliers: [],
            packageSuppliers: [],
            lastPackageSearch: null,
            itemsKey: 0,
        };
    },
    watch: {
        purchaseOrder: {
            handler(newValue, oldValue) {
                if (this.purchaseOrder.defaultPackageReference !== this.lastPackageSearch) {
                    this.updatePackageSuppliers()
                }
            },
            deep: true
        },
    },
    computed: {
        supplierSearch() {
            const query = this.purchaseOrder.supplier.toLowerCase().replace(/"/gm, ' ').replace('#supplier:', '').split(/[^A-Za-z0-9.-]+/);
            if (query.length) {
                return {
                    'package': this.packageSuppliers.filter((supplier) => {
                        return query.every((token) => {
                            return supplier.toLowerCase().includes(token) && supplier.trim()
                        });
                    }),
                    'others': this.suppliers.filter((supplier) => {
                        return !this.packageSuppliers.includes(supplier) && query.every((token) => {
                            return supplier.toLowerCase().includes(token) && supplier.trim()
                        });
                    })
                };
            }
            return {
                'package': this.packageSuppliers.filter((supplier) => supplier.trim()),
                'others': this.suppliers.filter((supplier) => {
                    return !this.packageSuppliers.includes(supplier) && supplier.trim()
                })
            }
        },
        tabs() {
            return [
                {value: "overview", label: "Overview"},
                {value: "notes", label: "Notes"},
                {value: 'files', label: 'Internal Files'},
            ];
        },
        combineSendLogs() {
            return this.purchaseOrder.sendLog.map((a) => {
                return a;
            }).sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        },
        revisionData() {
            return this.purchaseOrder.revisions.map((a, index) => {
                return {
                    date: a.lastUpdatedOn,
                    data: 'Revision ' + (index + 1) + ' created at ' + moment(a.lastUpdatedOn).format('DD/MM/YYYY HH:mm')
                }
            }).sort((a, b) => moment(b.date).unix() - moment(a.date).unix())

        }
    },
    methods: {
        download() {
            window.location.href = this.purchaseOrder.getDownloadUrl()
        },
        toggleDiscountMode() {
            this.discountAsPercentage = !this.discountAsPercentage;
        },
        attachUrl(id) {
            return route('api.v2.procurement.purchase-order.file.attach', {
                'id': this.item.id,
                'file_id': id
            })
        },
        detachUrl(id) {
            return route('api.v2.procurement.purchase-order.file.detach', {
                'id': this.item.id,
                'file_id': id
            })
        },
        downloadUrl(id) {
            return route('api.v2.procurement.purchase-order.file.download', {
                'id': this.item.id,
                'file_id': id
            })
        },
        async getPurchaseOrderFiles() {
            let data = await PurchaseOrder.find(this.item.id)
            return data.files
        },
        revoke(i) {
            this.purchaseOrder.revoke(i.id);
        },
        resend(i) {
            this.purchaseOrder.resend(i.id);
        },
        isActive(tabValue) {
            return tabValue === this.activeTab
        },
        select(tabValue) {
            this.activeTab = tabValue
        },
        reset() {
            this.activeTab = this.active
        },
        setAllPackage(op) {
            let update = async () => {
                this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                    item.packageReference = op.packageItem
                        ? op.packageItem.referenceNumber
                        : null;
                    item.packageName = op.packageItem ? op.packageItem.name : null;
                });
                this.purchaseOrder.defaultPackageReference = op.packageItem
                    ? parseInt(op.packageItem.referenceNumber)
                    : null;
                this.purchaseOrder.defaultPackageName = op.packageItem ? op.packageItem.name : null;

                await this.update();
            }
            if (this.purchaseOrder.items.some((item) => item.resolves.length)) {
                this.$modals.confirm({
                    title: `Do you want to update the Package?`,
                    button: 'Yes, update the Package',
                    paragraphs: ['This will clear ALL of the proposed RFM resolves on ALL items as they will no longer be valid when changing the package.'],
                    handle: async () => {
                        this.purchaseOrder.items.forEach((item) => {
                            item.resolves = []
                        })
                        await update();
                        this.overviewRenderKey++;
                    }
                })
            } else {
                update();
            }
        },
        setAllAddresses({addressData}) {
            this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                this.setAddress(item, addressData);
            });
            this.update();
        },
        updateAllDates() {
            this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                item.deliveryOption = item.deliveryOption || {
                    isDelivery: false,
                    address: null,
                    date: null,
                };
                item.deliveryOption.date =
                    item.deliveryOption.date === this.allDate ? null : this.allDate;
            });
            this.update();
        },
        updateAllAddresses(items, addressData) {
            this.updateAddresses(items.concat([this.defaultItem]), addressData);
            this.update();
        },
        addAllAddress({addressData}) {
            this.updateAddresses(
                this.purchaseOrder.items.concat([this.defaultItem]),
                addressData
            );
            this.update();
        },
        toggleAllCollection(p) {
            this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                if (!item.deliveryOption) {
                    item.deliveryOption = {
                        isDelivery: false,
                        address: null,
                        date: null,
                    };
                }
                item.deliveryOption.isDelivery = p;
            });
            this.update();
        },
        clearAllAddress() {
            this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                item.deliveryOption = null;
            });
            this.update();
        },
        loadAllDeliveryOptionsFromFirstItem() {
            if (!this.purchaseOrder.items.length) {
                if (this.defaultItem) {
                    this.allDeliveryOption = this.defaultItem.deliveryOption;
                    this.allDate = this.defaultItem.date;
                    this.allPackageReference = this.defaultItem.packageReference
                        ? this.defaultItem.packageReference
                        : this.purchaseOrder.defaultPackageReference;
                    this.allPackageName = this.defaultItem.packageName
                        ? this.defaultItem.packageName
                        : this.purchaseOrder.defaultPackageName;
                }
                return;
            }
            this.allDeliveryOption = this.purchaseOrder.items[0].deliveryOption;
            this.allDate = this.purchaseOrder.items[0].date;
            this.allPackageReference = this.purchaseOrder.items[0].packageReference;
            this.allPackageName = this.purchaseOrder.items[0].packageName;
        },
        togglePerItem() {
            if (!this.perItemToggle) {
                if (!this.areAllDeliveryOptionsSame()) {
                    this.$modals.confirm({
                        title:
                            "Warning - not all items have all the same package and delivery options",
                        paragraphs: [
                            "There are multiple line items in this Purchase order that have different related packages and delivery options.",
                            "Removing individual options will set ALL package and delivery options to that of line item #1 OVERWRITING any other package and delivery option data!",
                            "Any proposed RFM resolves will also be cleared.",
                            "Are you sure you want todo this?",
                        ],
                        button: "Yes, Set ALL to the same as line item #1",
                        handle: async () => {
                            this.purchaseOrder.items.forEach((i) => {
                                i.deliveryOption = cloneDeep(this.allDeliveryOption);
                                i.packageName = cloneDeep(this.allPackageName);
                                i.packageReference = cloneDeep(this.allPackageReference);
                                i.date = cloneDeep(this.allDate);
                                i.resolves = [];
                            });
                            this.calculateAddress();
                            this.update();
                            this.overviewRenderKey++;
                            this.perItemToggle = false;
                        },
                    });
                    this.perItemToggle = true;
                }
            }
        },
        areAllDeliveryOptionsSame() {
            let comp = false;
            return this.purchaseOrder.items.every((item) => {
                let localComp = {
                    packageName: item.packageName,
                    packageReference: item.packageReference,
                    deliveryOption: null,
                    date: item.date,
                };
                if (item.deliveryOption) {
                    localComp.deliveryOption = {
                        isDelivery: item.deliveryOption.isDelivery,
                        address: item.deliveryOption.address,
                        contactName: item.deliveryOption.contactName,
                        contactNumber: item.deliveryOption.contactNumber,
                    };
                }
                if (!comp) {
                    comp = JSON.stringify(localComp);
                }
                return JSON.stringify(localComp) === comp;
            });
        },
        openPackageSearch(open) {
            if (!this.readonly) {
                open();
                this.$nextTick(() => {
                    this.$refs.packageSearch.focus();
                });
            }
        },
        openAddressSearch(open) {
            if (!this.readonly) {
                open();
            }
        },
        openDatePicker() {
            if (!this.readonly) {
                this.$refs.datepicker.flatpickr.open();
            }
        },
        addressLines(deliveryOption) {
            let data = deliveryOption.address.trim();
            data = data.replace(/\n/g, ", ").trim();
            data = data.replace(/, ,/g, "").split(", ");
            let option = data.splice(-1, 1);
            data = data.map((item) => {
                return item.trim() + ",";
            });
            if (option.length > 0) {
                data.push(option[0]);
            }
            return data;
        },
        getRevisionsAsDropDown() {
            let op = this.purchaseOrder.revisions.map((i) => {
                return {
                    label: '#' + this.purchaseOrder.reference + (i.reference ? '-r' + i.reference : ''),
                    value: i.reference ? i.reference : 0,
                }
            })
            op.push({
                label: '#' + this.purchaseOrder.reference + (this.purchaseOrder.revisions.length ? '-r' + this.purchaseOrder.revisions.length : ''),
                value: -1
            })
            return op;
        },
        async viewRevision(e) {
            this.purchaseOrder = await PurchaseOrder.find(this.purchaseOrder.id)
            let ref = this.purchaseOrder.reference
            e = parseInt(e);
            if (e >= 0) {
                Object.assign(this.purchaseOrder, this.purchaseOrder.revisions[e]);
                this.purchaseOrder.fullReference = '#' + ref + (this.purchaseOrder.reference ? '-r' + e : '');
                this.purchaseOrder.reference = ref;
            } else if (e < 0) {
                this.purchaseOrder.fullReference = '#' + ref + (this.purchaseOrder.revisions.length ? ('-r' + this.purchaseOrder.revisions.length) : '');
                this.purchaseOrder.reference = ref;
            }
            this.overviewRenderKey++;
        },
        toggleEditMode(mode) {
            this.readonly = !mode;
            this.datePickerRenderKey++;
        },
        async save() {
            this.debouncedUpdate();
        },
        deleteTask(tIndex, mode) {
            if (mode === 'clear') {
                this.purchaseOrder.items = this.purchaseOrder.items.filter((i) => i.task !== tIndex)
            } else if (mode === 'up') {
                this.purchaseOrder.items.forEach((i) => {
                    if (i.task === tIndex) {
                        i.task--;
                    }
                })
            } else if (mode === 'down') {
                this.purchaseOrder.items.forEach((i) => {
                    if (i.task === tIndex) {
                        i.task++;
                    }
                })
            }
            this.purchaseOrder.tasks.splice(tIndex, 1);
            this.purchaseOrder.items.forEach((i) => {
                if (i.task > tIndex) {
                    i.task--;
                }
            })
            this.update();
            this.taskRenderKey++;

        },
        dropTask(e) {
            // the tasks have been moved - need to update all matching items
            let shuffleUp = e.oldIndex < e.newIndex
            this.purchaseOrder.items.forEach((i) => {
                let min = Math.min(e.oldIndex, e.newIndex);
                let max = Math.max(e.oldIndex, e.newIndex);
                if ((i.task >= min) && (i.task <= max)) {
                    if (shuffleUp) {
                        if (i.task > e.oldIndex) {
                            i.task--
                        } else if (i.task === e.oldIndex) {
                            i.task = e.newIndex
                        }
                    } else {
                        if (i.task < e.oldIndex) {
                            i.task++
                        } else if (i.task === e.oldIndex) {
                            i.task = e.newIndex
                        }
                    }
                }
            })

            // to keep it clean - sort the array by task, then index.
            this.purchaseOrder.items.sort((a, b) => a.task - b.task)
            this.update();
            this.taskRenderKey++;
        },
        drop(e) {
            // need to calculate Task Index + inner index into global indexes
            if (!this.purchaseOrder.useGroups) {
                // remove any tasks at this point
                this.purchaseOrder.items.forEach((i) => i.task = 0);
                this.purchaseOrder.tasks.splice(1, this.purchaseOrder.tasks.length - 1);
            }
            let fromTask = 0;
            let toTask = 0;
            if (this.$refs.dropTarget.length > 1) {
                fromTask = this.$refs.dropTarget.findIndex((item) => item.$el === e.from)
                toTask = this.$refs.dropTarget.findIndex((item) => item.$el === e.to)
            }
            let fromIndex = this.purchaseOrder.items.findIndex((item) => item.task === fromTask) + e.oldIndex

            let toIndex = this.purchaseOrder.items.findIndex((item) => item.task === toTask)

            if (toIndex === -1) {
                toIndex = this.purchaseOrder.items.findIndex((item) => item.task > toTask)
                if (toIndex === -1) {
                    toIndex = this.purchaseOrder.items.length - 1;
                } else {
                    toIndex += e.newIndex - 1;
                }
            } else {
                toIndex += e.newIndex;
            }

            if (toIndex >= this.purchaseOrder.items.length) {
                let k = toIndex - this.purchaseOrder.items.length + 1;
                while (k--) {
                    this.purchaseOrder.items.push(undefined);
                }
            }
            if (toIndex === -1) {
                toIndex = 0;
            }
            this.purchaseOrder.items.splice(toIndex, 0, this.purchaseOrder.items.splice(fromIndex, 1)[0]);
            this.purchaseOrder.items[toIndex].task = toTask
            this.purchaseOrder.items = this.purchaseOrder.items.filter((i) => i)
            // to keep it clean - sort the array by task, then index.
            this.purchaseOrder.items.sort((a, b) => a.task - b.task)
            this.update()
        },
        highlight(string) {
            string = "" + string;
            if (
                !string ||
                !(typeof string === "string" || string instanceof String)
            ) {
                return "&nbsp;";
            }
            let htmlSafe = string.replace(
                /[\u00A0-\u9999<>\&;0123456789#]/g,
                (i) => "&#" + i.charCodeAt(0) + ";"
            );
            if (this.search.trim() === "") {
                return htmlSafe;
            }
            let search = this.search
                .toLowerCase()
                .replace(/"/gm, ' ').replace('#supplier:', '')
                .replace(
                    /[\u00A0-\u9999<>\&;0123456789#]/g,
                    (i) => "&#" + i.charCodeAt(0) + ";"
                );
            const query = search.split(/[\s]+/).filter((item) => {
                return item.trim() !== "";
            });
            let reg = new RegExp(query.join("|"), "gi");
            let res = htmlSafe.replace(reg, "<span class='bg-blue-200'>$&</span>");
            if (res === "<span class='bg-blue-200'></span>") {
                return "&nbsp;";
            }

            return res;
        },
        addressUpdated(addressData) {
            addressData.items.forEach((item) => {
                item.deliveryOption.address = addressData.address;
                item.deliveryOption.contactName = addressData.contactName;
                item.deliveryOption.contactNumber = addressData.contactNumber;
            });
            this.update();
        },
        updateAddresses(items, addressData) {
            items.forEach((item) => {
                if (!item.deliveryOption) {
                    item.deliveryOption = {
                        isDelivery: true,
                        address: null,
                        date: null,
                    };
                }
                item.deliveryOption.address = addressData.address;
                item.deliveryOption.contactNumber = addressData.contactNumber;
                item.deliveryOption.contactName = addressData.contactName;
            });
            this.calculateAddress(true);
            this.update();
        },
        setAddress(item, addressData) {
            if (!item.deliveryOption) {
                item.deliveryOption = {
                    isDelivery: true,
                    address: null,
                    date: null,
                };
            }
            item.deliveryOption.address = addressData.address;
            item.deliveryOption.contactNumber = addressData.contactNumber;
            item.deliveryOption.contactName = addressData.contactName;
            this.calculateAddress(true);
            this.update();
        },
        setAllVatTo(vat) {
            this.purchaseOrder.items.forEach((i) => {
                console.log(this.$refs['item-' + i.id][0])
                this.$refs['item-' + i.id][0].$data.itemClone.vatPercentage = vat;
                this.$refs['item-' + i.id][0].updateVatPercentage(true);
            })
            this.itemsKey++;
        },
        clearAddress(item) {
            item.deliveryOption = null;
            this.calculateAddress(true);
            this.update();
        },
        addAddress(item, addressData) {
            this.setAddress(item, addressData);
        },
        addTask() {
            this.purchaseOrder.tasks.push(this.newTask.name)
            this.$nextTick(() => {
                this.$refs.task[this.$refs.task.length - 1].focus();
                this.newTask.name = '';
            });
            this.save();
        },
        addItem(tIndex) {
            let pos;
            if (tIndex === -1) {
                tIndex = this.purchaseOrder.tasks.length - 1;
                pos = this.purchaseOrder.items.length;
            } else {
                pos = this.purchaseOrder.items.findIndex((item) => item.task > tIndex)
                if (pos === -1) {
                    pos = this.purchaseOrder.items.length;
                }
            }
            let lastPackageReference, lastPackageName;
            let lastDeliveryOption = null;
            if (pos <= this.purchaseOrder.items.length && this.purchaseOrder.items.length) {
                lastPackageReference =
                    this.purchaseOrder.items[pos - 1].packageReference;
                lastPackageName =
                    this.purchaseOrder.items[pos - 1].packageName;
                lastDeliveryOption =
                    this.purchaseOrder.items[pos - 1].deliveryOption;
            } else {
                lastPackageReference = this.defaultItem.packageReference;
                lastPackageName = this.defaultItem.packageName;
                lastDeliveryOption = this.defaultItem.deliveryOption;
            }
            if (!lastPackageReference) {
                lastPackageReference = this.purchaseOrder.defaultPackageReference;
                lastPackageName = this.purchaseOrder.defaultPackageName;
            }
            let id = uuid()
            this.purchaseOrder.items.splice(pos, 0, {
                id: id,
                description: this.newItem,
                packageReference: lastPackageReference,
                packageName: lastPackageName,
                deliveryOption: lastDeliveryOption
                    ? cloneDeep(lastDeliveryOption)
                    : null,
                quantity: 0,
                unitOfMeasure: "",
                rate: "£0.00",
                net: "£0.00",
                vatValue: "£0.00",
                vatPercentage: this.purchaseOrder.items.length ? this.purchaseOrder.items[this.purchaseOrder.items.length - 1].vatPercentage : "20%",
                value: "£0.00",
                resolves: [],
                task: tIndex
            });
            this.$nextTick(() => {
                this.$refs['item-' + id][0].$refs.description.focus();
                this.newItem = "";
            });
            this.save();
        },
        async update() {
            this.busy = true;
            await this.purchaseOrder.update();
            Object.assign(this.item, await PurchaseOrder.find(this.purchaseOrder.id));
            this.busy = false;
            this.purchaseOrder.lastUpdatedBy = this.item.lastUpdatedBy;
            this.purchaseOrder.lastUpdatedOn = this.item.lastUpdatedOn;
            this.purchaseOrder.locked = this.item.locked;
            this.purchaseOrder.total = this.item.total;
            this.purchaseOrder.vatTotal = this.item.vatTotal;
            this.purchaseOrder.netTotal = this.item.netTotal;
            this.loadAllDeliveryOptionsFromFirstItem();
            this.metadataRenderKey++;
        },
        calculateAddress(allowBlank = false) {
            let s = [];
            this.purchaseOrder.items.concat([this.defaultItem]).forEach((item) => {
                if (item.deliveryOption && !isNull(item.deliveryOption.address)) {
                    let i = s.find(
                        (i) =>
                            i.address === item.deliveryOption.address &&
                            i.contactName === item.deliveryOption.contactName &&
                            i.contactNumber === item.deliveryOption.contactNumber
                    );
                    if (!i) {
                        i = {
                            address: item.deliveryOption.address,
                            contactName: item.deliveryOption.contactName,
                            contactNumber: item.deliveryOption.contactNumber,
                            items: [],
                        };
                        s.push(i);
                    }
                    i.items.push(item);
                }
            });
            this.addresses = s;
        },
        openDeleteModal() {
            const title =
                "Are you sure you want to remove Purchase Order " +
                this.item.fullReference +
                "?";
            const description =
                "You are removing Purchase Order #" +
                this.item.id +
                ". Are you sure you want to do this?";
            eventBus.$emit("delete-modal-open", title, description, async () => {
                await this.purchaseOrder.delete();
                this.removeItem(this.item);
            });
        },
        openDeleteRevisionModal() {
            const title =
                "Are you sure you want to remove Purchase Order Revision " +
                this.item.fullReference +
                "?";
            const description =
                "You are removing the draft revision on Purchase Order #" +
                this.item.id +
                ". Are you sure you want to do this?";
            eventBus.$emit("delete-modal-open", title, description, async () => {
                await this.purchaseOrder.delete();
                this.purchaseOrder = await PurchaseOrder.find(this.item.id);
                Object.assign(this.item, this.purchaseOrder);
                this.sentlogRenderKey++;
            });
        },
        async revise() {
            await this.purchaseOrder.revise();
            Object.assign(this.item, this.purchaseOrder)
            this.toggleEditMode(true);
            this.renderKey++;
            this.sentlogRenderKey++;
        },
        async pushToClassic() {
            await this.purchaseOrder.markAsSent();
        },
        async duplicate() {
            await this.purchaseOrder.duplicate();
            await this.updateItems()
            this.fetch(this.purchaseOrder.id)
        },
        async openSendModal() {
            this.$modals.open("purchase-order-send", {
                title: `Send Purchase Order`,
                button: "Send Purchase Order",
                purchaseOrder: this.purchaseOrder,
                handle: async (purchaseOrder, modal) => {
                    Object.assign(this.item, this.purchaseOrder);
                    this.renderKey++;
                    modal.close();
                    this.sentlogRenderKey++;
                    this.purchaseOrder.pushToClassic();
                },
            });
        },
        async openMarkAsSentModal() {
            this.$modals.confirm({
                title: `Manually mark Purchase Order as Sent?`,
                paragraphs: [
                    'Sent purchase orders can no longer be edited. Are you sure you want to proceed?',
                ],
                handle: async () => {
                    await this.purchaseOrder.markAsSent()
                    Object.assign(this.item, this.purchaseOrder);
                    this.renderKey++;
                    this.purchaseOrder.pushToClassic();
                },
            });
        },
        openVoidModal() {
            this.$modals.confirm({
                title:
                    "Are you sure you want to void Purchase Order " +
                    this.item.fullReference +
                    "?",
                paragraphs: [
                    "You are about to mark Purchase Order #" +
                    this.item.id +
                    " as VOIDED. No further changes (including new revisions) can be done once voided! Are you sure you want to do this?",
                ],
                button: "Yes, Mark as VOIDED",
                handle: async () => {
                    await this.purchaseOrder.void();
                    Object.assign(this.item, this.purchaseOrder);
                    this.renderKey++;
                },
            });
        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'PurchaseOrder') && (data.aggregate.id === this.purchaseOrder.id)) {
                    Object.assign(this.purchaseOrder, data.aggregate.data)
                    Object.assign(this.item, data.aggregate.data)
                }
            }
        },
        async updatePackageSuppliers() {
            let ref = this.purchaseOrder ? this.purchaseOrder.defaultPackageReference : null;
            this.lastPackageSearch = ref;
            this.packageSuppliers = ref ? await PurchaseOrder.suppliers(ref) : [];
        }
    }
    ,
    async mounted() {
        this.purchaseOrder = await PurchaseOrder.find(this.item.id);
        this.loadAllDeliveryOptionsFromFirstItem();
        this.state = "loaded";
        this.perItemToggle = !this.areAllDeliveryOptionsSame();
        this.calculateAddress(false);
        if (this.purchaseOrder) {
            User.registerAggregate({'type': 'PurchaseOrder', 'id': this.purchaseOrder.id}, this.updateFromListener)
        }
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("mode") === 'edit') {
            this.toggleEditMode(true);
        }
        let url = new URL(window.location.href);
        this.suppliers = await PurchaseOrder.suppliers(null)
        if (this.purchaseOrder.packageReference) {
            await this.updatePackageSuppliers();
        }
        history.replaceState(history.state, '', url.href);
    }
    ,
}
;
</script>